export type AnnouncementTone = "info" | "warning" | "critical";

export type Announcement = {
  id: string;
  tone?: AnnouncementTone;
  title: string;
  description: string;
  icon?: string;
  dueAt?: string;
  startsAt?: string;
  endsAt: string;
  link?: { label: string; href: string };
  dismissible?: boolean;
};

export const ANNOUNCEMENTS: readonly Announcement[] = [
  {
    id: "maintenance-2026-10-04",
    tone: "warning",
    icon: "construction",
    title: "Scheduled maintenance",
    description:
      "We're performing scheduled maintenance. Screening and monitoring may be briefly unavailable while it runs.",
    dueAt: "2026-10-04T12:00:00Z",
    endsAt: "2026-10-04T12:30:00Z",
  },
];

function parseInstant(iso: string | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function isAnnouncementActive(announcement: Announcement, now: Date): boolean {
  const ends = parseInstant(announcement.endsAt);
  if (!ends || now.getTime() >= ends.getTime()) return false;
  const starts = parseInstant(announcement.startsAt);
  return !starts || now.getTime() >= starts.getTime();
}

export function activeAnnouncements(
  now: Date,
  announcements: readonly Announcement[] = ANNOUNCEMENTS,
): Announcement[] {
  return announcements.filter((a) => isAnnouncementActive(a, now));
}

export function lastMondayUtc(now: Date): Date {
  const monday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  monday.setUTCDate(monday.getUTCDate() - ((monday.getUTCDay() + 6) % 7));
  return monday;
}

export function msUntilNextMondayUtc(now: Date): number {
  const next = lastMondayUtc(now);
  next.setUTCDate(next.getUTCDate() + 7);
  return next.getTime() - now.getTime();
}

export function isDismissalStale(dismissedAt: Date, now: Date): boolean {
  return lastMondayUtc(now).getTime() > dismissedAt.getTime();
}

export function msUntilNextAnnouncementChange(
  now: Date,
  announcements: readonly Announcement[] = ANNOUNCEMENTS,
): number | null {
  const boundaries = announcements
    .flatMap((a) => [a.startsAt, a.dueAt, a.endsAt])
    .map(parseInstant)
    .filter((d): d is Date => d !== null)
    .map((d) => d.getTime() - now.getTime())
    .filter((ms) => ms > 0);
  if (activeAnnouncements(now, announcements).some((a) => a.dismissible ?? true)) {
    boundaries.push(msUntilNextMondayUtc(now));
  }
  return boundaries.length ? Math.min(...boundaries) : null;
}

const UTC_DATE_TIME = new Intl.DateTimeFormat("en-US", {
  weekday: "short",
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: "UTC",
});

export function formatDueAt(iso: string): { utc: string } | null {
  const due = parseInstant(iso);
  if (!due) return null;
  const utc = `${UTC_DATE_TIME.format(due)} UTC`;
  return { utc };
}

const RELATIVE = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export function formatDueStatus(iso: string, now: Date): string | null {
  const due = parseInstant(iso);
  if (!due) return null;
  const diff = due.getTime() - now.getTime();
  if (diff <= 0) return "In progress";
  if (diff < HOUR) return RELATIVE.format(Math.max(1, Math.round(diff / MINUTE)), "minute");
  if (diff < DAY) return RELATIVE.format(Math.round(diff / HOUR), "hour");
  return RELATIVE.format(Math.round(diff / DAY), "day");
}
